import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Gem, Sparkles, ArrowUpCircle, Trophy, BookOpen } from 'lucide-react';
import { searchApi } from '../../lib/api';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

const CATEGORY_ICONS = {
    relics: Gem,
    gachas: Sparkles,
    upgrades: ArrowUpCircle,
    achievements: Trophy,
    tutorials: BookOpen,
};

const CATEGORY_COLORS = {
    relics: 'text-cyan-400',
    gachas: 'text-purple-400',
    upgrades: 'text-green-400',
    achievements: 'text-amber-400',
    tutorials: 'text-blue-400',
};

export const GlobalSearch = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen(true);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const searchDebounce = setTimeout(async () => {
            if (query.length >= 2) {
                setLoading(true);
                try {
                    const response = await searchApi.search(query);
                    setResults(response.data);
                } catch (error) {
                    console.error('Search error:', error);
                }
                setLoading(false);
            } else {
                setResults([]);
            }
        }, 300);

        return () => clearTimeout(searchDebounce);
    }, [query]);

    const handleResultClick = (result) => {
        const category = result.search_category;
        navigate(`/${category}/${result.id}`);
        setIsOpen(false);
        setQuery('');
        setResults([]);
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-colors"
            >
                <Search size={18} />
                <span className="text-sm flex-1 text-left">Suchen...</span>
                <kbd className="px-2 py-0.5 text-xs bg-white/10 rounded">⌘K</kbd>
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                onClick={() => setIsOpen(false)}
            />
            
            {/* Search Modal */}
            <div className="relative w-full max-w-2xl mx-4 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                {/* Search Input */}
                <div className="flex items-center gap-3 p-4 border-b border-white/10">
                    <Search size={20} className="text-gray-400" />
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Suche nach Relics, Gachas, Achievements..."
                        className="flex-1 bg-transparent border-0 text-lg focus-visible:ring-0 placeholder:text-gray-500"
                    />
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-1 text-gray-400 hover:text-white"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Results */}
                <div className="max-h-[60vh] overflow-y-auto">
                    {loading && (
                        <div className="p-8 text-center text-gray-500">
                            Suche...
                        </div>
                    )}
                    
                    {!loading && query.length >= 2 && results.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                            Keine Ergebnisse für "{query}"
                        </div>
                    )}

                    {!loading && results.length > 0 && (
                        <div className="p-2">
                            {results.map((result) => {
                                const Icon = CATEGORY_ICONS[result.search_category] || Gem;
                                const colorClass = CATEGORY_COLORS[result.search_category] || 'text-gray-400';
                                return (
                                    <button
                                        key={`${result.search_category}-${result.id}`}
                                        onClick={() => handleResultClick(result)}
                                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors text-left"
                                    >
                                        <div className={cn("p-2 rounded-lg bg-white/5", colorClass)}>
                                            <Icon size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-medium truncate">{result.title}</p>
                                            <p className="text-sm text-gray-500 capitalize">{result.search_category}</p>
                                        </div>
                                        {result.rarity && (
                                            <span className="text-xs text-gray-400 px-2 py-1 bg-white/5 rounded">
                                                {result.rarity}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {!loading && query.length < 2 && (
                        <div className="p-8 text-center text-gray-500">
                            Mindestens 2 Zeichen eingeben
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-white/10 flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-4">
                        <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded">↵</kbd> Auswählen</span>
                        <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded">ESC</kbd> Schließen</span>
                    </div>
                    <span>{results.length} Ergebnisse</span>
                </div>
            </div>
        </div>
    );
};
